import { useMutation } from "@tanstack/react-query";
import { BookOpenCheck, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { RecoveryCodeNotice } from "../components/RecoveryCodeNotice";
import { api } from "../lib/api";
import { queryClient } from "../lib/query";
import type { AuthResult } from "../lib/types";

type Mode = "login" | "register" | "recover";

export function AuthPage() {
  const [mode, setMode] = useState<Mode>("login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingResult, setPendingResult] = useState<AuthResult | null>(null);

  const authenticate = useMutation({
    mutationFn: async (): Promise<AuthResult> => {
      if (mode === "login") return api.login({ phone, password });
      if (mode === "register") return api.register({ phone, password, nickname: nickname.trim(), acceptedTerms: true });
      return api.recover({ phone, recoveryCode: recoveryCode.trim().toUpperCase(), newPassword: password });
    },
    onSuccess: async (result) => {
      if (result.recoveryCode) {
        setPendingResult(result);
        return;
      }
      await finishAuthentication(result);
    },
  });
  const guest = useMutation({
    mutationFn: api.enter,
    onSuccess: finishAuthentication,
  });

  async function finishAuthentication(result: AuthResult) {
    queryClient.setQueryData(["session"], result);
    await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    authenticate.mutate();
  }

  function switchMode(next: Mode) {
    setMode(next);
    setPassword("");
    setRecoveryCode("");
    authenticate.reset();
  }

  if (pendingResult?.recoveryCode) {
    return <main className="auth-screen"><section className="auth-panel"><RecoveryCodeNotice code={pendingResult.recoveryCode} onContinue={() => void finishAuthentication(pendingResult)} /></section></main>;
  }

  const title = mode === "login" ? "欢迎回来" : mode === "register" ? "创建家长账号" : "找回密码";

  return (
    <main className="auth-screen">
      <section className="auth-panel" aria-labelledby="auth-title">
        <div className="auth-brand"><BookOpenCheck size={34} /><span>作业管家</span></div>
        <h1 id="auth-title">{title}</h1>
        <p className="auth-subtitle">{mode === "recover" ? "输入注册手机号和保存的恢复码" : "登录后可在不同设备安全查看孩子的作业计划"}</p>

        {mode !== "recover" ? (
          <div className="auth-tabs" role="tablist" aria-label="账号操作">
            <button type="button" role="tab" aria-selected={mode === "login"} onClick={() => switchMode("login")}>登录</button>
            <button type="button" role="tab" aria-selected={mode === "register"} onClick={() => switchMode("register")}>注册</button>
          </div>
        ) : null}

        <form className="auth-form" onSubmit={submit}>
          {mode === "register" ? (
            <label><span>家长称呼</span><input autoComplete="name" maxLength={40} value={nickname} placeholder="例如：小宇妈妈" onChange={(event) => setNickname(event.target.value)} required /></label>
          ) : null}
          <label><span>手机号</span><input autoFocus inputMode="tel" autoComplete="tel" pattern="1[3-9][0-9]{9}" maxLength={11} value={phone} placeholder="请输入 11 位手机号" onChange={(event) => setPhone(event.target.value.replace(/\D/g, ""))} required /></label>
          {mode === "recover" ? (
            <label><span>账号恢复码</span><input autoCapitalize="characters" autoComplete="off" maxLength={14} value={recoveryCode} placeholder="XXXX-XXXX-XXXX" onChange={(event) => setRecoveryCode(event.target.value.toUpperCase())} required /></label>
          ) : null}
          <label><span>{mode === "recover" ? "设置新密码" : "密码"}</span><span className="password-field"><input type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={8} maxLength={64} value={password} placeholder="至少 8 个字符" onChange={(event) => setPassword(event.target.value)} required /><button type="button" aria-label={showPassword ? "隐藏密码" : "显示密码"} onClick={() => setShowPassword((value) => !value)}>{showPassword ? <EyeOff /> : <Eye />}</button></span></label>
          {mode === "register" ? (
            <label className="agreement-check"><input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} required /><span>我已阅读并同意 <Link to="/legal/terms" target="_blank" rel="noreferrer">用户协议</Link>、<Link to="/legal/privacy" target="_blank" rel="noreferrer">隐私政策</Link>和<Link to="/legal/children" target="_blank" rel="noreferrer">儿童信息说明</Link></span></label>
          ) : null}
          {authenticate.isError ? <p className="form-error" role="alert">{authenticate.error.message}</p> : null}
          <button className="primary-button" type="submit" disabled={authenticate.isPending || (mode === "register" && !agreed)}>{authenticate.isPending ? "请稍候…" : mode === "login" ? "登录" : mode === "register" ? "注册并进入" : "重设密码并登录"}</button>
          {mode === "login" ? <button className="auth-text-button" type="button" onClick={() => switchMode("recover")}>忘记密码？使用恢复码找回</button> : null}
          {mode === "recover" ? <button className="auth-text-button" type="button" onClick={() => switchMode("login")}>返回登录</button> : null}
        </form>

        {mode !== "recover" ? (
          <><div className="auth-divider"><span>或</span></div><button className="guest-button" type="button" disabled={guest.isPending} onClick={() => guest.mutate()}>{guest.isPending ? "正在进入…" : "快速体验（无需注册）"}</button>{guest.isError ? <p className="form-error" role="alert">{guest.error.message}</p> : null}</>
        ) : null}
        <p className="auth-security"><ShieldCheck size={16} /> 密码和恢复码均加密保存</p>
      </section>
    </main>
  );
}
